import router from '@/router';
import {
  SuccessNotification,
  ErrorNotification,
} from '@/store/modules/notifications/notifications';
import axios from 'axios';
import { actions, mutations } from './types';

// Note the interaction of the following value with:
//  -POLL_INTERVAL_MS in ui/src/store/modules/collaboration/actions.js
//  -JWT_ACCESS_TOKEN_EXPIRES in api/config.py
//  -JWT_REFRESH_TOKEN_EXPIRES in api/config.py

// call /auth/refresh to get new access token if access token ttl drops below this value
const MIN_TTL_MS = 60 * 1000; // 1 minute
var JWT_REFRESH_PENDING = false;

// log the user out if no API activity in this time
const IDLE_LOGOUT_MS = 30 * 60 * 1000; // 30 minutes
// time of last API response that indicates user activity
var LAST_USER_API_RESPONSE_TIME = null;
var SIGNOUT_PENDING = false;

// convert baseURL to unique cookie name prefix
const baseURL = process.env.BASE_URL;
let cookiePrefix = '';
if (baseURL) {
  const match = baseURL.match(/^\/?(.*)$/);
  if (match) {
    cookiePrefix = match[1].replace('/', '_');
  }
}

// refresh JWT access token
function refreshJWT(commit, dispatch) {
  if (!JWT_REFRESH_PENDING) {
    JWT_REFRESH_PENDING = true;
    setTimeout(() => {
      axios
        .post(
          '/auth/refresh',
          {},
          {
            // refresh expects the CSRF refresh token not the access token
            xsrfCookieName: cookiePrefix + 'csrf_refresh_token',
          }
        )
        .then(function(response) {
          JWT_REFRESH_PENDING = false;
          return response;
        })
        .catch(function(err) {
          // unable to refresh - refresh token may have expired, normally this should not occur
          JWT_REFRESH_PENDING = false;
          dispatch(actions.SIGN_USER_OUT, {
            error: 'Session expired: please log in again.',
          });
          return Promise.reject(err);
        });
    }, 1000);
  }
}

function signOut(commit, dispatch, msg) {
  if (!SIGNOUT_PENDING) {
    SIGNOUT_PENDING = true;
    setTimeout(() => {
      SIGNOUT_PENDING = false;
      dispatch(actions.SIGN_USER_OUT, {
        message: msg,
      });
    }, 1000);
  }
}

var INTERCEPTOR_INIT_DONE = false;

// intercept /api/user_feed requests to get jwt_ttl_seconds and trigger a refresh if needed
function initInterceptor(commit, dispatch) {
  if (INTERCEPTOR_INIT_DONE) {
    return;
  }
  axios.interceptors.response.use(
    function(resp) {
      let url = resp.config.url;
      if (url == '/api/user_feed') {
        // check whether user should be logged out due to inactivity
        if (LAST_USER_API_RESPONSE_TIME) {
          var idle_ms = Date.now() - LAST_USER_API_RESPONSE_TIME;
          if (idle_ms > IDLE_LOGOUT_MS) {
            signOut(commit, dispatch, 'Signing out due to inactivity.');
            return resp;
          }
        }

        // check if JWT access token refresh needed
        if ('data' in resp && 'jwt_ttl_seconds' in resp.data) {
          let ttl_secs = resp.data['jwt_ttl_seconds'];
          if (ttl_secs * 1000 < MIN_TTL_MS) {
            refreshJWT(commit, dispatch);
          }
        }
      }
      // additional API paths that should be ignored because they are accessed automatically on a timer
      else if (url.includes('channels/general/messages')) {
        // no-op
      }
      // count any other /api path access as evidence of user activity
      else if (url.startsWith('/api/') || url.startsWith('/auth/signin')) {
        LAST_USER_API_RESPONSE_TIME = Date.now();
      }
      return resp;
    },
    // check for session expiration/forced logout
    function(err) {
      let url = err.config.url;
      if (
        !url.startsWith('/auth/') &&
        'data' in err.response &&
        'msg' in err.response.data
      ) {
        let msg = err.response.data.msg;
        if (msg.startsWith('Token has') || msg.startsWith('Missing cookie')) {
          commit(mutations.CLEAR_USER);
          router.push('/signin');
        }
      }
      return Promise.reject(err);
    }
  );
  INTERCEPTOR_INIT_DONE = true;
}

export default {
  /**
   *  Create user account.
   * @param {Object} commit
   * @param {Object} payload Email, password, name, and institution.
   *
   *  Returns promises to allow caller add logic based on action.
   */
  async [actions.CREATE_USER_ACCOUNT]({ commit }, payload) {
    commit(mutations.SET_LOADING, true);
    commit(mutations.CLEAR_AUTH_ERROR);

    const { email, password, name, institution } = payload;

    try {
      const { data } = await axios.post('/auth/signup', {
        email,
        password,
        name,
        institution,
      });
      commit(mutations.SET_USER, {
        ...data.user,
      });
      commit(mutations.SET_LOADING, false);
      return new Promise(resolve => resolve());
    } catch (err) {
      let error = err.response.data;
      if (
        typeof error === 'object' &&
        !(error instanceof String) &&
        'error' in error
      ) {
        error = err.response.data['error'];
      }
      commit(mutations.SET_AUTH_ERROR, error);
      commit(mutations.SET_LOADING, false);
      return new Promise((resolve, reject) => reject());
    }
  },
  /**
   *  Sign the user in.
   * @param {*} commit
   * @param {*} payload Email and password
   * Returns promises to allow caller add logic based on action.
   */
  async [actions.SIGN_USER_IN]({ commit, dispatch }, { email, password }) {
    commit(mutations.SET_LOADING, true);
    commit(mutations.CLEAR_AUTH_ERROR);

    try {
      const { data } = await axios.post('/auth/signin', {
        email,
        password,
      });
      commit(mutations.SET_USER, data.user);
      commit(mutations.SET_LOADING, false);
      initInterceptor(commit, dispatch);

      return new Promise(resolve => resolve());
    } catch (err) {
      const { error } = err.response.data;
      commit(mutations.SET_AUTH_ERROR, error);
      commit(mutations.SET_LOADING, false);

      return new Promise((resolve, reject) => reject());
    }
  },
  /**
   * Sign the user out.
   * @param {Object} commit
   * @param {*} payload: 'error' or 'message'
   */
  async [actions.SIGN_USER_OUT]({ commit, dispatch }, payload) {
    commit(mutations.SET_LOADING, true);
    try {
      await axios.delete('/auth/signout');
      commit(mutations.CLEAR_USER);
      router.push('/signin');
      var signout_message = 'You have successfully logged out.';
      var notification = null;

      if (payload && 'error' in payload) {
        notification = new ErrorNotification(payload['error']);
      } else {
        if (payload && 'message' in payload) {
          signout_message = payload['message'];
        }
        notification = new SuccessNotification(signout_message);
      }
      dispatch(notification.dispatch, notification, {
        root: true,
      });
    } catch (err) {
      // what should we do when this errors?
    }
    commit(mutations.SET_LOADING, false);
  },
  /**
   * Set user if there is an active session.
   * @param {Object} commit
   */
  async [actions.GET_USER_FROM_SESSION]({ commit, dispatch }) {
    commit(mutations.SET_LOADING, true);

    try {
      const { data } = await axios.get('/auth/signin');
      commit(mutations.SET_USER, data.user);
      commit(mutations.SET_LOADING, false);
      initInterceptor(commit, dispatch);

      return new Promise(resolve => resolve());
    } catch (err) {
      const { data } = err.response;
      // TODO:
      // Need a nicer way to check for what happened on server
      // (is there no cookie or has that cookie w/ token expired?)
      if (data.msg.includes('expired')) {
        const error = new ErrorNotification('Your session has expired.');
        dispatch(error.dispatch, error, { root: true });
      }
      commit(mutations.CLEAR_USER);
      commit(mutations.SET_LOADING, false);

      return new Promise((resolve, reject) => reject());
    }
  },
};
